import { BillingReturn } from './room/billing-return.js';
/* eslint-disable import/no-internal-modules */
import { Network } from '#~/app/components/network/index.js';
import { Protect } from '#~/auth/index.js';
import { useSessionEnded } from '#~/auth/use-session-ended.js';
import { EmailVerificationGate } from '#~/auth/email-verification-gate/index.js';
import { ProfileSynchronizer } from '#~/auth/synchronizer.js';
import { useConfig } from '#~/config/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
import { Provider as NatsProvider } from '#~/nats/provider/index.js';
import { Panel as Notifications } from '#~/notifications/components/panel/index.js';
import { OnboardingGate } from '#~/onboarding/index.js';
import { Loader } from '@studio/design-system/loader';
import { type ReactElement } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Auth as LinkAuth } from './agent/link/auth.js';
import { Complete as LinkComplete } from './agent/link/complete.js';
import { Configure as LinkConfigure } from './agent/link/configure.js';
import { Confirm as LinkConfirm } from './agent/link/confirm.js';
import { CreateStudio as LinkCreateStudio } from './agent/link/create-studio.js';
import { SelectStudio as LinkSelectStudio } from './agent/link/select-studio.js';
import { Linking } from './agent/linking/linking.js';
import { Restart } from './agent/restart/restart.js';
import { Download as SetupDownload } from './agent/setup/download.js';
import { Link as SetupLink } from './agent/setup/link.js';
// ENG-4175: post-auth first-login segmentation wizard, rendered before download.
import { PostAuthPrequal } from './agent/setup/post-auth-prequal.js';
import { Root as SetupRoot } from './agent/setup/setup.js';
import { Select as SetupSelect } from './agent/setup/select.js';
import { ExitNavigator } from './agent/exit-navigator.js';
import { Shutdown } from './agent/shutdown/shutdown.js';
import { Unlink } from './agent/unlink/unlink.js';
import { Update } from './agent/update/update.js';
import { ProtectBuildInfo } from './build-info/protect-build-info.js';
import { Paths } from './constants.js';
import { AppAuth } from './electron/app-auth.jsx';
import { ElectronLogin } from './electron/login.jsx';
import { ElectronProtect } from './electron/protect.jsx';
import { ElectronTest } from './electron/test.js';
import { ElectronTray } from './electron/tray/tray.js';
import { Exhibits } from './exhibits/exhibits.js';
import { PostRedeem as InvitePostRedeem } from './invite/post-redeem.js';
import { PreRedeem as InvitePreRedeem } from './invite/pre-redeem.js';
import { Login } from './login/login.jsx';
import { LoginCollision } from './login/collision.jsx';
import { Logout } from './logout/logout.js';
import { NotFound } from './not-found/not-found.js';
import { Register } from './register/register.jsx';
import { AgentLookupAndWait } from './room/agent-lookup-and-wait.js';
import { AgentLookup } from './room/agent-lookup.js';
import { RoomBoot } from './room/room-boot.js';
import { RoomInspect } from './room/room-inspect.js';
import { Room } from './room/room.js';
import { SelectStudioRoute } from './select-studio/select-studio.js';
import { Root } from './root/root.js';
import { BootOverlay } from './boot-overlay/boot-overlay.js';
import { BootReadyLayout } from './boot-overlay/boot-ready-layout.js';
import { AutoLinkGuard } from './verify-auto-link/v2/auto-link-guard.js';

/** Application router. */
export const Router = (): ReactElement => {
  useSessionEnded();
  const { isProduction } = useConfig();

  const handleRenderingExhibitComponents = (): ReactElement | null => {
    if (!isProduction) {
      return <Route element={<Exhibits />} path={Paths.exhibits.$} />;
    }

    return null;
  };

  return (
    <>
      <ExitNavigator />
      <Routes>
        {/* Auth routes related to login, signup, etc. */}
        <Route element={<Login />} path={Paths.login} />
        {/* ENG-3451: public collision page — must stay OUTSIDE <Protect>: the
         * user lands here precisely because sign-in failed. */}
        <Route element={<LoginCollision />} path={Paths.loginCollision} />
        <Route element={<Logout />} path={Paths.logout} />
        <Route element={<Register />} path={Paths.register} />

        {/* Protected routes */}
        <Route element={<Protect />}>
          {/* Boot-protected subtree: BootOverlay sits over routes whose
           * landing UI is "Loading your studio..." — index/Root, Room, setup,
           * link, invite, lookup, linking. Per gyfky-W1: single-mount;
           * opacity dismisses via the union of autoLinkState + isRootReady
           * + hasRegistryLoaded. */}
          <Route
            element={
              <NatsProvider>
                <ProfileSynchronizer>
                  {/* ENG-3797: gate the app shell on email verification. Placed
                    inside ProfileSynchronizer (studio user resolved) and around
                    Network so it covers BOTH browser and Electron Protect
                    branches via the shared Outlet (spec-fragment.md §1).
                    BootOverlay (gyfky-W1, added on main) stays inside Network
                    so the boot-loading UX is unchanged once the gate passes.

                    It stays OUTSIDE the onboarding funnel on purpose: a user
                    who has not verified their email must not be walked through
                    first-run setup and left adding a camera to an account they
                    cannot yet use. Verification first, then the funnel. */}
                  <EmailVerificationGate>
                    <Network>
                      {/* OnboardingGate is inside Network because it reads the
                       * broker, the agent list and the linked room to decide
                       * whether the Bridge is ready — none of which exist above
                       * it. It sits outermost WITHIN that subtree so it covers
                       * every route and survives Root's bootstrap navigation to
                       * /:roomId/boot. BootOverlay nests inside it: the two are
                       * orthogonal, and "Loading your studio…" must still sit
                       * over boot-protected routes underneath the welcome
                       * modal. */}
                      <OnboardingGate>
                        <BootOverlay>
                          <Outlet />
                        </BootOverlay>
                      </OnboardingGate>
                    </Network>
                  </EmailVerificationGate>
                  <Notifications />
                </ProfileSynchronizer>
              </NatsProvider>
            }
          >
            <Route
              element={
                <AutoLinkGuard>
                  <Root />
                </AutoLinkGuard>
              }
              index
            />

            {/* Normal room routes stay boot-overlay protected but OUTSIDE the
             * generic ready layout: room components own their visible-ready
             * signal after room data/agent gates resolve. Per
             * ai.knowledge/global rules `code-comments-required`,
             * `reference-rules-in-comments`. */}
            <Route element={<Room />} path={Paths.room.$} />
            <Route element={<RoomBoot />} path={Paths.room.boot.$} />

            {/* ENG-4186: non-room deep-link routes publish
             * `boot.isRootReady = true` on mount via this single layout wrapper,
             * so a direct deep-link into any of them (e.g. the add-embedded-device
             * wizard) dismisses <BootOverlay> even though <Root> never mounted.
             * <Root> (index, above) stays OUTSIDE the wrapper — it is the sole
             * route that gates the overlay on isRootReady itself. Normal room
             * routes also stay OUTSIDE the wrapper because room routes own their
             * visible-ready signal. See boot-ready-layout.tsx. */}
            <Route element={<BootReadyLayout />}>
              <Route element={<RoomInspect />} path={Paths.room.inspect.$} />

              {/* ENG-4037 workspace-entry picker (Variant A consumer). */}
              <Route element={<SelectStudioRoute />} path={Paths.selectStudio} />

              {/* Build Info route */}
              {<Route element={<ProtectBuildInfo />} path={Paths.buildInfo} />}

              {/* Agent setup routes */}
              <Route element={<SetupRoot />} path={Paths.agent.setup.$} />
              {/* Desktop registration returns to Iris without the web questionnaire. */}
              <Route
                element={isElectronApp() ? <Navigate replace to='/' /> : <PostAuthPrequal />}
                path={Paths.agent.setup.prequal}
              />
              <Route element={<SetupSelect />} path={Paths.agent.setup.select} />
              <Route element={<SetupDownload />} path={Paths.agent.setup.download.$} />
              <Route element={<SetupLink />} path={Paths.agent.setup.link} />

              {/* Agent link routes */}
              <Route element={<LinkConfirm />} path={Paths.agent.link.confirm.$} />
              <Route element={<LinkConfigure />} path={Paths.agent.link.configure.$} />
              <Route element={<LinkComplete />} path={Paths.agent.link.complete.$} />
              <Route element={<LinkSelectStudio />} path={Paths.agent.link.selectStudio.$} />
              <Route element={<LinkAuth />} path={Paths.agent.link.auth.$} />
              <Route element={<LinkCreateStudio />} path={Paths.agent.link.createStudio.$} />

              {/* Invite routes */}
              <Route element={<InvitePreRedeem />} path={Paths.invite.preRedeem.$} />
              <Route element={<InvitePostRedeem />} path={Paths.invite.postRedeem.$} />

              <Route element={<Linking />} path={Paths.agent.linking} />

              <Route element={<AgentLookup />} path={Paths.agent.$} />
              <Route element={<AgentLookupAndWait />} path={Paths.agent.withWait.$} />
            </Route>
          </Route>

          {/* Exit / transition subtree: same providers, NO BootOverlay.
           * These routes render their own chrome (Shutdown shows "Shutting
           * down …", Restart shows its own message). They must NOT be
           * obscured by BootOverlay re-arming when the agent disconnects
           * during the exit sequence — that produced the regression where
           * the shutdown screen read "Loading your studio…" instead of
           * "Shutting down …". Carved out from the boot-protected subtree
           * above; see test routes/router-exit-routes.test.tsx. */}
          <Route
            element={
              <NatsProvider>
                <ProfileSynchronizer>
                  <Network>
                    <Outlet />
                  </Network>
                  <Notifications />
                </ProfileSynchronizer>
              </NatsProvider>
            }
          >
            {/* Payment identity must be processed even while Studio boot is
             * unresolved. The return handler never activates that Studio. */}
            <Route element={<BillingReturn />} path={Paths.room.billingReturn.$} />
            <Route element={<Unlink />} path={Paths.agent.unlink} />
            <Route element={<Update />} path={Paths.agent.update} />
            <Route element={<Restart />} path={Paths.agent.restart} />
            <Route element={<Restart />} path={Paths.agent.restartWithAgent.$} />
            <Route element={<Shutdown />} path={Paths.agent.shutdown} />
          </Route>
        </Route>

        {handleRenderingExhibitComponents()}

        {/* Electron routes */}
        <Route element={<ElectronProtect />}>
          <Route element={<ElectronTray />} path='/electron/tray' />
          <Route element={<ElectronLogin />} path='/electron/login' />
          <Route element={<ElectronTest />} path='/electron/test' />
        </Route>

        {/* OAuth (Auth0) routes */}
        {/* Web Auth0 redirect_uri (root.tsx, login.tsx, register.tsx). AuthProvider
         * exchanges the code here, then navigates to `returnTo`; React Router
         * applies that navigation as a deferred transition, so auth-state
         * updates can re-render this path first. Without a match it fell
         * through to `*`, and the web NotFound throws (React #520 in PostHog). */}
        <Route element={<Loader />} path={Paths.oauth2.callback} />
        <Route element={<AppAuth />} path='/oauth/iris-studio' />
        <Route element={<AppAuth />} path='/oauth/iris-studio/:env' />
        <Route element={<NotFound />} path='*' />
      </Routes>
    </>
  );
};
